import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BackToTop from '../../components/feature/BackToTop';
import IntegrationHero from './components/IntegrationHero';
import TwelveMonthRoadmap from './components/TwelveMonthRoadmap';
import ImplementationTimeline from './components/ImplementationTimeline';
import SchoolBranding from './components/SchoolBranding';
import FAQSection from './components/FAQSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function AcademicIntegration() {
  useEffect(() => {
    document.title = 'Academic Integration | STEMForge Tech Labs | 12-Month Partnership Model';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Discover STEMForge 12-month academic integration model with structured implementation, teacher training, and continuous support for complete STEM transformation.');
    setMeta('keywords', 'academic integration, 12-month STEM partnership, school STEM transformation, teacher training program, STEMForge');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Academic Integration | STEMForge Tech Labs | 12-Month Partnership Model', 'property');
    setMeta('og:description', 'Discover STEMForge 12-month academic integration model with structured implementation, teacher training, and continuous support for complete STEM transformation.', 'property');
    setMeta('og:url', `${SITE_URL}/academic-integration`, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:title', 'Academic Integration | STEMForge Tech Labs | 12-Month Partnership Model');
    setMeta('twitter:description', 'Discover STEMForge 12-month academic integration model with structured implementation, teacher training, and continuous support for complete STEM transformation.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/academic-integration`;

    const schemaId = 'schema-academic';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/academic-integration`,
      url: `${SITE_URL}/academic-integration`,
      name: 'Academic Integration | STEMForge Tech Labs | 12-Month Partnership Model',
      description: 'Discover STEMForge 12-month academic integration model with structured implementation, teacher training, and continuous support for complete STEM transformation.',
      dateModified: '2026-04-10',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'STEMForge Tech Labs' },
      mainEntity: {
        '@type': 'Course',
        name: '12-Month STEM Academic Integration Program',
        description: 'A structured 12-month partnership model for complete STEM transformation in schools, including lab setup, teacher training, and continuous academic support.',
        provider: {
          '@type': 'Organization',
          name: 'STEMForge Tech Labs',
          url: SITE_URL,
        },
        educationalLevel: 'School',
        teaches: ['STEM Education', 'AI & Robotics', 'Innovation', 'Critical Thinking'],
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'onsite',
          duration: 'P12M',
        },
      },
    });

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const sectionId = sessionStorage.getItem('scrollToSection');
    if (sectionId) {
      sessionStorage.removeItem('scrollToSection');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <IntegrationHero />
      <TwelveMonthRoadmap />
      <ImplementationTimeline />
      <SchoolBranding />
      <FAQSection />
      <Footer />
      <BackToTop />
    </div>
  );
}
